import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, useReducedMotion } from 'motion/react'
import { useFormStore } from '../store/formStore'
import { requestOtp } from '../api/applicant'
import { ApiError } from '../api/client'
import { LogoHeader } from '../components/layout'
import { Button } from '../components/ui/Button'

const schema = z.object({
  email: z.string().email('Please enter a valid email address.'),
})

type FormData = z.infer<typeof schema>

export function LoginScreen() {
  const navigate = useNavigate()
  const setField = useFormStore((s) => s.setField)
  const shouldReduceMotion = useReducedMotion()

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })

  // Gate the Continue button: stays disabled until the email field has a value,
  // then switches to its primary (navy) state.
  const canContinue = (watch('email') ?? '').trim().length > 0

  const onSubmit = async (data: FormData) => {
    setField('authEmail', data.email)
    try {
      await requestOtp(data.email)
      navigate('/verify')
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        // A code was sent moments ago and is still valid — go enter it, with
        // the resend countdown seeded from the server's Retry-After.
        navigate('/verify', { state: { cooldownSeconds: err.retryAfterSeconds || 60 } })
        return
      }
      setError('email', {
        type: 'server',
        message: err instanceof ApiError ? err.message : 'Could not send the code. Please try again.',
      })
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.4,
        ease: [0.0, 0.0, 0.2, 1.0] as [number, number, number, number],
      },
    },
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-surface-base)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-4)',
      }}
    >
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        style={{
          background: 'var(--color-surface-container-lowest)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-ambient)',
          padding: 'var(--spacing-10)',
          width: '100%',
          maxWidth: '440px',
        }}
      >
        {/* Zota Logo */}
        <LogoHeader />

        {/* Heading */}
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--weight-semibold)',
            fontSize: 'var(--text-title-lg)',
            color: 'var(--color-text-primary)',
            lineHeight: 'var(--leading-tight)',
            marginBottom: 'var(--spacing-3)',
            margin: '0 0 var(--spacing-3) 0',
          }}
        >
          Start your application
        </h1>

        {/* Subtitle */}
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-md)',
            color: 'var(--color-text-secondary)',
            lineHeight: 'var(--leading-normal)',
            marginBottom: 'var(--spacing-8)',
            margin: '0 0 var(--spacing-8) 0',
          }}
        >
          Use your company email address to get started or continue your application.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Email Field */}
          <div style={{ marginBottom: 'var(--spacing-8)' }}>
            <label
              htmlFor="email"
              style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontWeight: 'var(--weight-medium)',
                fontSize: 'var(--text-label-md)',
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-2)',
              }}
            >
              Company email address
            </label>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@company.com"
              disabled={isSubmitting}
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error' : undefined}
              {...register('email')}
              style={{
                display: 'block',
                width: '100%',
                height: '48px',
                padding: '0 var(--spacing-4)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-md)',
                color: 'var(--color-text-primary)',
                background: 'var(--color-surface-container-lowest)',
                border: errors.email
                  ? '1px solid var(--color-error-border)'
                  : '1px solid var(--color-outline-subtle)',
                borderRadius: 'var(--radius-md)',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 150ms ease, box-shadow 150ms ease',
                opacity: isSubmitting ? 0.6 : 1,
              }}
              onFocus={(e) => {
                if (!errors.email) {
                  e.currentTarget.style.borderColor = 'var(--color-outline-focus)'
                  e.currentTarget.style.boxShadow = 'var(--shadow-focus-ring)'
                }
              }}
              onBlur={(e) => {
                if (!errors.email) {
                  e.currentTarget.style.borderColor = 'var(--color-outline-subtle)'
                  e.currentTarget.style.boxShadow = 'none'
                }
              }}
            />
            {errors.email && (
              <p
                id="email-error"
                role="alert"
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-label-sm)',
                  color: 'var(--color-error-text)',
                  marginTop: 'var(--spacing-2)',
                  margin: 'var(--spacing-2) 0 0 0',
                }}
              >
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Continue Button */}
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={!canContinue}
            style={{ width: '100%' }}
          >
            {isSubmitting ? 'Checking...' : 'Continue'}
          </Button>
        </form>

        {/* Footer privacy text */}
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-sm)',
            color: 'var(--color-text-tertiary)',
            textAlign: 'center',
            marginTop: 'var(--spacing-6)',
            margin: 'var(--spacing-6) 0 0 0',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          By continuing, you agree to Zota's
          <br />
          <a
            href="https://zota.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-action-primary)', fontWeight: 'var(--weight-semibold)', textDecoration: 'underline' }}
          >
            Privacy Policy
          </a>{' '}
          and{' '}
          <a
            href="https://zota.com/terms-of-use/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-action-primary)', fontWeight: 'var(--weight-semibold)', textDecoration: 'underline' }}
          >
            Terms of Use
          </a>
          .
        </p>
      </motion.div>
    </div>
  )
}
